import { useCallback, useRef, useState } from 'react'

/**
 * **Eine Auswahl gilt nur in ihrer Liste** — 7.2 Abschnitt 48, R1504.
 *
 * Der Befund vom 2026-10-09: Unter *Kontenrahmen* war *0210 Grundstücke und
 * Bauten* gewählt; der Wechsel auf *Bewegungsarten* öffnete das
 * Störungsfenster. Die Seite leerte ihre Auswahl erst *nach* dem Wechsel der
 * Liste — dazwischen lag ein Augenblick mit neuer Liste und altem Eintrag, und
 * genau in ihm fragte das Blatt nach `bewegungsart/KTO-0210`.
 *
 * **Hier gibt es diesen Augenblick nicht.** Die Wahl merkt sich, in welcher
 * Liste sie getroffen wurde. Wechselt die Liste, ist sie schon in derselben
 * Darstellung leer — kein Abruf sieht die alte Wahl unter der neuen Liste. Wer
 * zurückwechselt, findet sie nicht wieder.
 *
 * @param liste Wozu die Wahl gehört — Art der Stammdaten, Bewertungsebene.
 * @param leer Was „nichts gewählt" heißt — `null` oder `''`.
 * @param bleibt Ob eine Wahl auch in der neuen Liste gilt und stehen bleibt —
 *   etwa ein Positionsplan, der für beide Ebenen geführt wird (R614).
 */
export function useListenwahl<T>(
  liste: unknown,
  leer: T,
  bleibt?: (wert: T) => boolean,
): [T, (wert: T) => void] {
  const [stand, setStand] = useState<{ liste: unknown; wert: T }>({ liste, wert: leer })

  // Die Liste der letzten Darstellung: Wer wählt, wählt in ihr — auch aus
  // einem Haken heraus, der im selben Zug wie der Wechsel läuft.
  const jetzt = useRef(liste)
  jetzt.current = liste

  // Im selben Durchlauf nachgezogen, nicht in einem Haken danach: React
  // verwirft diese Darstellung und rechnet sie neu, bevor irgendein Abruf sie
  // sieht.
  const gleich = Object.is(stand.liste, liste)
  const gilt = gleich || (stand.wert !== leer && bleibt?.(stand.wert) === true)
  if (!gleich) {
    setStand({ liste, wert: gilt ? stand.wert : leer })
  }

  const setze = useCallback((wert: T) => setStand({ liste: jetzt.current, wert }), [])

  return [gilt ? stand.wert : leer, setze]
}
